(() => {
  'use strict';
  const key = Symbol.for('seldravinpack.cartDrawer.initialize');
  if (window[key]) { window[key](document); return; }
  const instances = new Map();
  function create(dialog) {
    const config = JSON.parse(dialog.querySelector('[data-cart-drawer-config]').textContent);
    const content = dialog.querySelector('[data-cart-content]');
    const status = dialog.querySelector('[data-cart-status]');
    const retry = dialog.querySelector('[data-cart-retry]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const listen = (node, type, callback) => node.addEventListener(type, callback, { signal: controller.signal });
    let busy = false, queuedRefresh = false, motion = null, opener = null, closing = false;
    function message(text, error = false) {
      status.textContent = text; status.hidden = !text; retry.parentElement.hidden = !error;
    }
    function lock(value) {
      busy = value; dialog.setAttribute('aria-busy', String(value));
      content.querySelectorAll('button, input').forEach(el => { el.disabled = value; });
      retry.disabled = value;
    }
    function applyMarkup(html) {
      if (!html) throw new Error(config.loadError);
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
      if (!next) throw new Error(config.loadError);
      const state = JSON.parse(next.querySelector('[data-cart-state]').textContent);
      content.replaceChildren(...next.childNodes); content.hidden = false;
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = state.count; });
      document.querySelectorAll('[data-cart-total]').forEach(el => { el.textContent = state.total; });
      document.querySelectorAll('.cart-trigger').forEach(el => { el.setAttribute('aria-label', state.label); });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: { item_count: state.count }, state } }));
    }
    async function read() {
      const url = new URL(config.cartUrl, location.origin);
      url.searchParams.set('section_id', dialog.dataset.sectionId);
      const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error(config.loadError);
      applyMarkup(await response.text());
    }
    function finish() {
      lock(false);
      if (queuedRefresh && dialog.isConnected) { queuedRefresh = false; refresh(); }
    }
    async function refresh() {
      if (busy) { queuedRefresh = true; return; }
      lock(true); content.hidden = true; message(config.loading);
      try { await read(); message(''); }
      catch (error) { if (error.name !== 'AbortError') message(config.loadError, true); }
      finally { finish(); }
    }
    function show(trigger) {
      if (!dialog.open) opener = trigger || document.activeElement;
      closing = false; motion?.cancel();
      document.querySelectorAll('dialog.product-dialog[open]').forEach(el => el.close());
      if (!dialog.open) {
        dialog.showModal();
        if (!reduced.matches) motion = dialog.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], { duration: 260, easing: 'ease-out' });
      }
      refresh();
    }
    function close() {
      if (!dialog.open || closing) return;
      closing = true; motion?.cancel();
      if (reduced.matches) { dialog.close(); return; }
      motion = dialog.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: 200, easing: 'ease-in' });
      motion.onfinish = () => dialog.close();
    }
    async function change(line, quantity) {
      if (busy) return;
      const input = line.querySelector('[data-cart-quantity]');
      if (quantity !== 0 && !input.reportValidity()) return;
      const lineKey = line.dataset.lineKey;
      const index = [...content.querySelectorAll('[data-line-key]')].indexOf(line);
      const active = document.activeElement;
      const selector = active?.matches('[data-cart-remove]') ? '[data-cart-remove]' : active?.matches('[data-cart-step]') ? `[data-cart-step="${active.dataset.cartStep}"]` : '[data-cart-quantity]';
      lock(true); message(config.updating);
      try {
        const endpoint = new URL(config.changeUrl, location.origin); endpoint.pathname += '.js';
        const response = await fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: controller.signal,
          body: JSON.stringify({ id: lineKey, quantity, sections: [dialog.dataset.sectionId], sections_url: new URL(config.cartUrl, location.origin).pathname })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(typeof cart.description === 'string' ? cart.description : config.updateError);
        // Never repeat a successful mutation if its section rendering failed.
        try {
          if (cart.sections?.[dialog.dataset.sectionId]) applyMarkup(cart.sections[dialog.dataset.sectionId]);
          else await read();
        } catch { content.hidden = true; throw new Error(config.loadError); }
        message(config.updated);
        if (dialog.open) {
          const lines = [...content.querySelectorAll('[data-line-key]')];
          const target = lines.find(el => el.dataset.lineKey === lineKey) || lines[Math.min(index, lines.length - 1)];
          lock(false);
          (target?.querySelector(selector) || content.querySelector('[data-cart-close]') || dialog.querySelector('[data-cart-close]')).focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          // The response may have been lost after Shopify changed the cart. Reload before another edit.
          content.hidden = true; message(error.message || config.updateError, true);
        }
      } finally { finish(); }
    }
    listen(document, 'cart:added', event => show(event.detail?.opener));
    listen(document, 'click', event => {
      const trigger = event.target.closest('a.cart-trigger');
      if (!trigger || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault(); show(trigger);
    });
    listen(dialog, 'click', event => {
      if (event.target.closest('[data-cart-close]')) { close(); return; }
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) { close(); return; }
      const line = event.target.closest('[data-line-key]');
      if (!line || busy) return;
      if (event.target.closest('[data-cart-remove]')) { change(line, 0); return; }
      const step = event.target.closest('[data-cart-step]');
      if (step) {
        const input = line.querySelector('[data-cart-quantity]'), before = input.value;
        if (Number(step.dataset.cartStep) > 0) input.stepUp(); else input.stepDown();
        if (input.value !== before) change(line, Number(input.value));
      }
    });
    listen(dialog, 'change', event => {
      if (event.target.matches('[data-cart-quantity]') && event.target.reportValidity()) change(event.target.closest('[data-line-key]'), Number(event.target.value));
    });
    listen(dialog, 'submit', event => {
      if (busy || content.hidden || event.submitter?.name !== 'checkout') event.preventDefault();
    });
    listen(retry, 'click', refresh);
    listen(dialog, 'cancel', event => { event.preventDefault(); close(); });
    listen(dialog, 'close', () => {
      motion?.cancel(); closing = false;
      if (opener?.isConnected) opener.focus({ preventScroll: true }); else document.querySelector('.cart-trigger')?.focus();
    });
    listen(reduced, 'change', () => { if (closing) dialog.close(); motion?.cancel(); });
    return { destroy() { queuedRefresh = false; motion?.cancel(); controller.abort(); if (dialog.open) dialog.close(); } };
  }
  function initialize(scope) {
    const roots = [...scope.querySelectorAll('[data-cart-drawer]')];
    if (scope.matches?.('[data-cart-drawer]')) roots.unshift(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, create(root)); });
  }
  function remove(root) { instances.get(root)?.destroy(); instances.delete(root); }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => { [...instances.keys()].forEach(root => { if (event.target === root || event.target.contains(root)) remove(root); }); });
  new MutationObserver(() => { [...instances.keys()].forEach(root => { if (!root.isConnected) remove(root); }); }).observe(document.documentElement, { childList: true, subtree: true });
  window[key] = initialize;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true }); else initialize(document);
})();
